import { useState } from "react";
import { sendInquiry } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../services/toast";

export default function InquiryModal({
  isOpen,
  onClose,
  visualizerToken,
  items = [], // Pass cart/selected products items array here
  quotationId = null,
  totals = {},
}) {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    country: "India",
    state: "",
    city: "",
    website_url: "",
    contact_country_code: "+91",
    contact_number: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Map selected items to expected backend schema
    const formattedItems = items.map((item) => ({
      region: item.region || "Wall",
      product_id: item.product_id || item.id,
      product_sku: item.sku || item.product_sku || "-",
      product_name: item.name || item.product_name || "Product",
      product_size:
        typeof item.product_size === "string"
            ? item.product_size
            : item.product_size?.name || "-",
      application_type: item.application_type || "-",
      pieces_per_box: Number(item.pieces_per_box || 0),
      coverage_area_per_box: String(item.coverage_area_per_box || 0),
      input_type: item.input_type || "box",
      input_value: Number(item.input_value || 1),
      area_sqft: Number(item.area_sqft || 0),
      area_sqm: Number(item.area_sqm || 0),
      boxes_needed: Number(item.boxes_needed || 1),
      price_per_box: Number(item.price_per_box || 0),
      price_per_sqm: Number(item.price_per_sqm || 0),
      total_price: Number(item.total_price || 0),
      price_unit: item.price_unit || "INR",
    }));

    const payload = {
      visualizer_token: visualizerToken,
      quotation_id: quotationId,
      full_name: formData.full_name,
      email: formData.email,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      website_url: formData.website_url,
      contact_country_code: formData.contact_country_code,
      contact_number: formData.contact_number,
      address: formData.address,
      message: formData.message,
      items: formattedItems,
      total_area_sqft: totals.total_area_sqft ?? 0,
      total_area_sqm: totals.total_area_sqm ?? 0,
      total_boxes: totals.total_boxes ?? 0,
      total_price: totals.total_price ?? 0,
    };

    try {
      await sendInquiry(visualizerToken, payload);
      showSuccessToast("Inquiry submitted successfully!");
      onClose();
    } catch (err) {
      showErrorToast(err.message || "Failed to submit inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">
            Send Order / Inquiry
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg"
          >
            ✕
          </button>
        </div>

        {error && <div className="mb-4 text-xs text-red-600">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs text-gray-700">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Full Name/ Company Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="full_name"
                required
                value={formData.full_name}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 font-medium">Country <span className="text-red-500">*</span></label>
              <select
                name="country"
                required
                value={formData.country}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900 bg-white"
              >
                <option value="India">India</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">State <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="state"
                required
                value={formData.state}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Website URL</label>
              <input
                type="url"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                placeholder="www.yourwebsite.com"
                className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Contact No <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="contact_country_code"
                  value={formData.contact_country_code}
                  onChange={handleChange}
                  className="w-16 rounded-md border border-gray-300 px-2 py-2 text-center outline-none focus:border-gray-900"
                />
                <input
                  type="tel"
                  name="contact_number"
                  required
                  value={formData.contact_number}
                  onChange={handleChange}
                  placeholder="1234 567 890"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">Address <span className="text-red-500">*</span></label>
            <textarea
              name="address"
              rows="3"
              required
              value={formData.address}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Message</label>
            <textarea
              name="message"
              rows="2"
              value={formData.message}
              onChange={handleChange}
              placeholder="Message for company"
              className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-gray-900"
            />
          </div>

          <div className="flex justify-end pt-3 border-t">
            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-gray-600 px-5 py-2 text-xs font-semibold text-white transition hover:bg-gray-700 disabled:opacity-50"
            >
              {loading ? "Submitting..." : "Submit Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}